import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import PremiumWelcomeModal from "../modals/PremiumWelcomeModal/PremiumWelcomeModal";
import usePlan from "../../hooks/usePlan";
import { MEMBER_PLAN } from "../../utils/memberPlan";

const STORAGE_KEY = "ds_premium_celebrated";

const isPremiumSubscription = (sub) =>
  sub?.subscription_type?.name === "premium" && sub?.is_enabled === true;

const subscriptionFingerprint = (sub) => {
  if (!sub) return null;
  return String(
    sub.id ??
      sub.uuid ??
      sub.stripe_subscription_id ??
      sub.started_at ??
      sub._optimisticAt ??
      "active",
  );
};

const readMarker = () => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

const writeMarker = (value) => {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* ignore */
  }
};

export default function PremiumTransitionWatcher() {
  const history = useHistory();
  const userId = useSelector((s) => s.user?.user?.id ?? null);
  const subscription = useSelector(
    (s) => s.user?.master_club_subscriber?.subscription ?? null,
  );

  const isPremium = isPremiumSubscription(subscription);
  const { plan, isPremiumLite } = usePlan();
  const fingerprint = isPremium ? subscriptionFingerprint(subscription) : null;

  // Tracks which userId's premium state we've already snapshotted as a
  // baseline. Reset to null on logout so a subsequent login is treated as
  // a fresh first-observation.
  const baselineUserIdRef = useRef(null);
  const previousPremiumRef = useRef(false);
  // Premium Lite → Premium keeps the type "premium": the plan tells it apart.
  const previousPlanRef = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Waiting for the user to load. On logout this drops back to null and
    // we reset the baseline so the next login is a fresh first-observation.
    if (!userId) {
      baselineUserIdRef.current = null;
      previousPremiumRef.current = false;
      previousPlanRef.current = null;
      return;
    }

    // First time we observe the premium state for this user in this session
    // (fresh app boot OR fresh login). A user who was already premium when
    // logging in is NOT transitioning — they just walked through the door
    // holding the ticket they already had. We capture the baseline silently
    // and write the localStorage marker so an in-session subscription_id
    // change doesn't re-trigger either.
    if (baselineUserIdRef.current !== userId) {
      baselineUserIdRef.current = userId;
      previousPremiumRef.current = isPremium;
      previousPlanRef.current = plan;
      if (isPremium && fingerprint) {
        writeMarker(`${userId}:${fingerprint}`);
      }
      return;
    }

    // From here on out we're looking for in-session transitions only.
    const wasPremium = previousPremiumRef.current;
    previousPremiumRef.current = isPremium;
    const wasLite = previousPlanRef.current === MEMBER_PLAN.PREMIUM_LITE;
    previousPlanRef.current = plan;

    // An upgrade from Premium Lite: welcome to Premium.
    if (isPremium && wasLite && plan === MEMBER_PLAN.PREMIUM) {
      const upgradeMarker = `${userId}:upgrade:${fingerprint || "active"}`;
      if (readMarker() !== upgradeMarker) {
        writeMarker(upgradeMarker);
        setOpen(true);
      }
      return;
    }

    if (!isPremium || wasPremium) return;

    const marker = `${userId}:${fingerprint || "active"}`;
    if (readMarker() === marker) return;

    writeMarker(marker);
    setOpen(true);
  }, [isPremium, fingerprint, userId, plan]);

  const onPrimary = () => {
    setOpen(false);
    // The welcome copy is about choosing goals; the page itself now opens on
    // Percorsi, so name the tab.
    history.push("/preferenze-nutrizionali?subtab=obiettivi");
  };

  return (
    <PremiumWelcomeModal
      isOpen={open}
      onPrimary={onPrimary}
      onDismiss={() => setOpen(false)}
      lite={isPremiumLite}
    />
  );
}
